import {
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type Ref,
} from "vue";

/**
 * Определяет, пора ли показывать кнопку «наверх» у скролл-контейнера.
 *
 * Кнопка должна появляться, когда первый элемент списка полностью ушёл за верхнюю границу
 * контейнера. Поэтому вместо scroll-обработчика вешаем IntersectionObserver
 * на элемент-якорь.
 *
 * Якорь ищется селектором внутри контейнера, а не передаётся ref-ом. Поиск по
 * data-атрибуту корня карточки даёт первый элемент в DOM-порядке при любой
 * вложенности.
 *
 * Список перерисовывается и первый элемент может смениться (например, в Работах
 * с подгрузкой вверх), поэтому наблюдатель переподключается по deps-триггеру.
 *
 * @param containerRef   ref на скролл-контейнер (может быть null)
 * @param anchorSelector селектор якоря внутри контейнера, напр. '[data-contact-id]'
 * @param deps           функция-триггер (напр. () => list.value) для переподключения при смене данных
 */
export function useScrollTopButton(
  containerRef: Ref<HTMLElement | null>,
  anchorSelector: string,
  deps?: () => unknown,
) {
  const isScrollTopEnabled = ref(false);

  let observer: IntersectionObserver | null = null;
  let observedEl: Element | null = null;

  const connect = () => {
    const container = containerRef.value;
    const anchor = container?.querySelector(anchorSelector) ?? null;

    if (observer && anchor === observedEl) {
      return;
    }

    observer?.disconnect();
    observer = null;
    observedEl = anchor;

    if (!container || !anchor) {
      isScrollTopEnabled.value = false;
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) {
          return;
        }

        if (!entry.target.isConnected) {
          return;
        }
        const rootTop = entry.rootBounds?.top ?? 0;
        isScrollTopEnabled.value =
          !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
      },
      {
        root: container,
        threshold: 0,
      },
    );
    observer.observe(anchor);
  };

  const scrollToTop = () => {
    containerRef.value?.scrollTo({ top: 0, behavior: "smooth" });
  };

  onMounted(() => {
    nextTick(connect);
  });

  watch(containerRef, () => nextTick(connect));

  if (deps) {
    watch(deps, () => nextTick(connect));
  }

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
    observedEl = null;
  });

  return { isScrollTopEnabled, scrollToTop };
}
